import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, Circle, Clock, ClipboardList } from "lucide-react";
import { formatDate } from "../lib/utils";

// Unauthenticated page — uses raw axios (no cookie auth needed).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function JourneyStep({ step, isCurrent }) {
  let Icon = Circle;
  if (step.reached && !isCurrent) Icon = CheckCircle2;
  else if (isCurrent) Icon = Clock;
  let textCls = "text-slate-500";
  if (step.reached && !isCurrent) textCls = "text-[#1A7F6E]";
  else if (isCurrent) textCls = "text-[#0D1B2A] font-semibold";
  return (
    <li className="flex gap-3" data-testid={`public-step-${step.key}`}>
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${step.reached ? "text-[#1A7F6E]" : "text-slate-300"}`} />
      <div>
        <p className={`text-sm ${textCls}`}>{step.label}</p>
        {isCurrent && <p className="text-[11px] text-slate-500 mt-0.5">In progress now</p>}
      </div>
    </li>
  );
}

function DocumentChecklist({ items, onToggle, busyKey }) {
  if (!items || items.length === 0) return null;

  // Group by category for readability
  const groups = {};
  for (const d of items) {
    const g = d.group || "Other";
    groups[g] = groups[g] || [];
    groups[g].push(d);
  }
  const confirmed = items.filter((d) => d.confirmed).length;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="document-checklist">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1 flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" /> Documents your consultant needs
            </p>
            <h3 className="text-lg font-semibold text-[#0D1B2A]">Tick off what you&apos;ve sent</h3>
            <p className="text-xs text-slate-500 mt-0.5">Your consultant will see each item as it&apos;s marked.</p>
          </div>
          <span className="text-xs font-semibold text-[#1A7F6E] whitespace-nowrap" data-testid="doc-progress">
            {confirmed} / {items.length}
          </span>
        </div>
        <div className="space-y-5">
          {Object.entries(groups).map(([g, docs]) => (
            <div key={g}>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{g}</p>
              <ul className="space-y-1.5">
                {docs.map((d) => (
                  <li key={d.key}>
                    <label
                      htmlFor={`doc-${d.key}`}
                      className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                        d.confirmed
                          ? "bg-[#E6F4F1]/60 border-[#1A7F6E]/40"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`doc-row-${d.key}`}
                    >
                      <Checkbox
                        id={`doc-${d.key}`}
                        checked={d.confirmed}
                        disabled={busyKey === d.key}
                        onCheckedChange={(v) => onToggle(d.key, Boolean(v))}
                        data-testid={`doc-check-${d.key}`}
                      />
                      <span className={`text-sm flex-1 ${d.confirmed ? "text-[#1A7F6E] line-through" : "text-[#0D1B2A]"}`}>
                        {d.name}
                      </span>
                      {d.confirmed && (
                        <span className="text-[10px] font-semibold text-[#1A7F6E]">SENT</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PublicStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [busyKey, setBusyKey] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/lead/${token}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [token]);

  async function toggleDoc(key, confirmed) {
    setBusyKey(key);
    try {
      await axios.post(`${API}/public/lead/${token}/document/${key}`, { confirmed });
      setData((d) => ({
        ...d,
        document_checklist: d.document_checklist.map((it) =>
          it.key === key ? { ...it, confirmed } : it),
      }));
    } catch (err) {
      console.warn("Doc toggle failed — will resync on refresh", err);
    } finally {
      setBusyKey("");
    }
  }

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center text-sm text-slate-500">
        Loading status…
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-4">
        <Card className="max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-semibold text-[#0D1B2A] mb-2">Link not found</h1>
            <p className="text-sm text-slate-500">
              This status link is invalid or has expired. Please contact your Next Provider consultant.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentIdx = data.pipeline.findIndex((p) => p.key === data.current_stage);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="public-status">
      <header className="bg-[#0D1B2A] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#1A7F6E] mb-1">Next Provider Australia</p>
            <h1 className="text-xl font-semibold">Registration progress</h1>
          </div>
          <span className="text-xs text-slate-300">nextprovider.com.au</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Client</p>
            <h2 className="text-2xl font-semibold text-[#0D1B2A]">{data.org_name}</h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Current milestone</p>
                <p className="text-sm font-medium text-[#1A7F6E] mt-1" data-testid="public-status-milestone">
                  {data.current_milestone}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Last update</p>
                <p className="text-sm text-[#0D1B2A] mt-1">
                  {data.stage_updated_at ? formatDate(data.stage_updated_at) : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Expected audit</p>
                <p className="text-sm text-[#0D1B2A] mt-1">
                  {data.audit_type
                    ? (data.audit_type === "CERTIFICATION" ? "Certification" : "Verification")
                    : "Not yet determined"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Journey</p>
            <ol className="space-y-4">
              {data.pipeline.map((step, i) => (
                <JourneyStep key={step.key} step={step} isCurrent={i === currentIdx} />
              ))}
            </ol>
          </CardContent>
        </Card>

        <DocumentChecklist
          items={data.document_checklist || []}
          onToggle={toggleDoc}
          busyKey={busyKey}
        />

        <p className="text-center text-xs text-slate-400">
          Questions? Reply to your consultant&apos;s email — this page updates automatically as your registration progresses.
        </p>
      </main>
    </div>
  );
}
